import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Container, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, FileDownload, InfoOutlined, PointOfSale, Print, Receipt, Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import { useNavigation } from "../contexts/RouterContext";
import { filterFieldSx } from "../theme/filterStyles";
import { colors } from "../theme/designSystem";
import { maskName } from "../utils/nameMask";
import {
  dayAttendancesService, DayAttendanceGroup, DayAttendanceOption, DayAttendanceRow, DayAttendancesFilters, DayAttendancesReport,
} from "../services/dayAttendancesService";

/**
 * Resumo atendimentos dia (RN-223, item 194): todos os atendimentos de um dia com o status — inclusive os não
 * executados —, o convênio/plano e o pagamento (situação, forma e data). Excluído da agenda não entra.
 */

const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const hoje = () => localIso(new Date());
const somaDias = (iso: string, dias: number) => {
  const [y, m, d] = iso.split("-").map(Number);
  return localIso(new Date(y, m - 1, d + dias));
};
const moeda = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const hora = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
const dataBr = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

const SITUACOES = [
  { value: "", label: "Todas" },
  { value: "Pago", label: "Pago" },
  { value: "Pendente", label: "Pendente" },
];

const situacaoCor: Record<string, { bg: string; fg: string }> = {
  Pago: { bg: colors.successSoft, fg: "#155724" },
  Pendente: { bg: colors.warningSoft, fg: colors.warningSoftText },
};

const StatusChip: React.FC<{ label: string; color: string | null }> = ({ label, color }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      height: 22, fontSize: "0.72rem", fontWeight: 600, maxWidth: "100%",
      bgcolor: color || "#e9ecef", color: color ? "#fff" : "#495057",
      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
    }}
  />
);

const Bloco: React.FC<{ titulo: string; grupos: DayAttendanceGroup[]; comValor?: boolean }> = ({ titulo, grupos, comValor }) => (
  <Box sx={{ flex: "1 1 220px", minWidth: 0, border: "1px solid #e9ecef", borderRadius: "8px", p: 1.5, backgroundColor: "#fff" }}>
    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>{titulo}</Typography>
    {grupos.length === 0 ? (
      <Typography sx={{ fontSize: 12, color: "#adb5bd" }}>—</Typography>
    ) : grupos.map((g) => (
      <Box key={g.name} sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 12.5, py: 0.25 }}>
        <span style={{ overflowWrap: "anywhere" }}>{g.name}</span>
        <span style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
          {g.count}{comValor ? ` · ${moeda(g.total)}` : ""}
        </span>
      </Box>
    ))}
  </Box>
);

const MultiFiltro: React.FC<{ label: string; value: string[]; options: DayAttendanceOption[]; onChange: (v: string[]) => void }> =
  ({ label, value, options, onChange }) => (
    <TextField
      select size="small" label={label} value={value} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}
      onChange={(e) => {
        const v = e.target.value as unknown;
        onChange(typeof v === "string" ? v.split(",").filter(Boolean) : (v as string[]));
      }}
      SelectProps={{
        multiple: true,
        displayEmpty: true,
        renderValue: (selected) => {
          const ids = selected as string[];
          if (ids.length === 0) return "Todos";
          if (ids.length > 2) return `${ids.length} selecionados`;
          return options.filter((o) => ids.includes(o.id)).map((o) => o.name).join(", ");
        },
      }}
    >
      {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
    </TextField>
  );

const ReportDayAttendances: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();

  const [date, setDate] = useState(hoje);
  const [healthPlans, setHealthPlans] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [professionalId, setProfessionalId] = useState("");
  const [paymentSituation, setPaymentSituation] = useState("");
  const [showNames, setShowNames] = useState(false);
  const [data, setData] = useState<DayAttendancesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(ITENS_POR_PAGINA_PADRAO);

  const filtros: DayAttendancesFilters = useMemo(
    () => ({ date, healthPlans, paymentMethods, statuses, professionalId, paymentSituation }),
    [date, healthPlans, paymentMethods, statuses, professionalId, paymentSituation]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setData(await dayAttendancesService.get(filtros));
      setPagina(1);
    } catch {
      setErro("Não foi possível carregar o relatório.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const linhas = useMemo(() => data?.items ?? [], [data]);
  const daPagina = useMemo(() => linhas.slice((pagina - 1) * porPagina, pagina * porPagina), [linhas, pagina, porPagina]);
  const nome = (n: string) => (showNames ? n : maskName(n));

  const temFiltro = date !== hoje() || healthPlans.length > 0 || paymentMethods.length > 0 || statuses.length > 0
    || professionalId !== "" || paymentSituation !== "";

  const limpar = () => {
    setDate(hoje());
    setHealthPlans([]);
    setPaymentMethods([]);
    setStatuses([]);
    setProfessionalId("");
    setPaymentSituation("");
  };

  const exportar = async () => {
    try {
      const blob = await dayAttendancesService.exportXlsx(filtros);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `resumo_atendimentos_${date}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setErro("Não foi possível gerar a planilha.");
    }
  };

  // Atalho só para quem abre a tela Financeiro do paciente (Administrador e Secretário, RN-543)
  const podeAbrirFinanceiro = (user?.allowedPages || []).includes("patient-financial-page");
  const abrirFinanceiro = (patientId: string) =>
    window.open(`${window.location.pathname}?page=patient-financial-page&patientId=${patientId}`, "_blank", "noopener");
  const abrirRecibo = (paymentId: string) =>
    window.open(`${window.location.pathname}?page=payment-receipt&id=${paymentId}`, "_blank", "noopener");

  return (
    <Box className="c4u-print-page" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Resumo atendimentos dia</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Todos os atendimentos do dia, com o status, o convênio/plano e o pagamento.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                  <Tooltip title="Dia anterior" arrow>
                    <IconButton size="small" onClick={() => setDate(somaDias(date, -1))} aria-label="Dia anterior"><ChevronLeft /></IconButton>
                  </Tooltip>
                  <TextField size="small" label="Dia" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }} />
                  <Tooltip title="Próximo dia" arrow>
                    <IconButton size="small" onClick={() => setDate(somaDias(date, 1))} aria-label="Próximo dia"><ChevronRight /></IconButton>
                  </Tooltip>
                </Box>
                <MultiFiltro label="Convênio / plano" value={healthPlans} options={data?.plans ?? []} onChange={setHealthPlans} />
                <MultiFiltro label="Tipo de pagamento" value={paymentMethods} options={data?.paymentMethodOptions ?? []} onChange={setPaymentMethods} />
                <MultiFiltro label="Status" value={statuses} options={data?.statusOptions ?? []} onChange={setStatuses} />
                <TextField select size="small" label="Profissional" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">Todos</MenuItem>
                  {(data?.professionals ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Situação do pagamento" value={paymentSituation} onChange={(e) => setPaymentSituation(e.target.value)} fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}>
                  {SITUACOES.map((o) => <MenuItem key={o.value || "todas"} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={limpar} disabled={!temFiltro || loading} />
                <Button
                  variant="contained" size="small" onClick={() => setShowNames((v) => !v)}
                  startIcon={showNames ? <VisibilityOff /> : <Visibility />}
                  sx={{ textTransform: "none", backgroundColor: "#6c757d", "&:hover": { backgroundColor: "#5a6268" }, height: 40 }}
                >
                  {showNames ? "Ocultar nomes" : "Exibir nomes"}
                </Button>
                <Tooltip title="Imprimir" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => window.print()} disabled={loading || linhas.length === 0} aria-label="Imprimir">
                      <Print />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar para Excel" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={exportar} disabled={loading || linhas.length === 0} aria-label="Exportar">
                      <FileDownload />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <Box className="c4u-info-box no-print">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Typography>
                Entram todos os atendimentos do dia, inclusive os não executados (pendentes de confirmação, cancelados,
                faltas, sem presença). Só o que foi excluído da agenda fica fora. Pagamento: "Pago" tem baixa, "Pendente"
                é presença que cobra e ainda não foi paga, "—" não gera cobrança. A data do pagamento pode ser outro dia
                (antecipado ou depois); o que entrou no caixa em cada dia está no relatório Recebimentos.
              </Typography>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : erro ? (
              <Typography sx={{ textAlign: "center", color: "#dc3545", py: 4 }}>{erro}</Typography>
            ) : (
              <>
                <Box sx={summaryCardsRowSx} className="c4u-print-block">
                  <ReportSummaryCard label="Atendimentos do dia" value={String(data?.count ?? 0)} sub={data ? dataBr(`${data.date}T12:00:00`) : ""} />
                  <ReportSummaryCard label="Realizado" value={moeda(data?.realized ?? 0)} sub="presenças que cobram" />
                  <ReportSummaryCard label="Pago" value={moeda(data?.paid ?? 0)} subColor="#155724" />
                  <ReportSummaryCard label="Pendente" value={moeda(data?.pending ?? 0)} subColor="#dc3545" />
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }} className="c4u-print-block">
                  <Bloco titulo="Por confirmação" grupos={data?.byConfirmation ?? []} />
                  <Bloco titulo="Por presença" grupos={data?.byPresence ?? []} />
                  <Bloco titulo="Pago por forma de pagamento" grupos={data?.byPaymentMethod ?? []} comValor />
                  <Bloco titulo="Por convênio / plano" grupos={data?.byPlan ?? []} comValor />
                </Box>

                <ResultCount shown={linhas.length} />

                {/* Tela: colunas agrupadas (data com horário, profissional com serviço, pagamento com forma e
                    data) para não amontoar. A impressão continua com a tabela de colunas separadas, abaixo. */}
                <Box className="c4u-table no-print">
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 104px" }}>Data</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 170px" }}>Paciente</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "1 1 190px" }}>Profissional / serviço</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 1 130px" }}>Convênio / plano</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 176px" }}>Status</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 170px" }}>Pagamento</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 104px", textAlign: "right" }}>Valor final</Box>
                    <Box className="admin-plans-header-cell admin-plans-actions" sx={{ flex: "0 0 92px" }}>Ações</Box>
                  </Box>

                  {daPagina.length === 0 ? (
                    <Typography sx={{ px: 2, py: 3, fontSize: 13, color: "#adb5bd", textAlign: "center" }}>
                      Nenhum atendimento neste dia com os filtros escolhidos.
                    </Typography>
                  ) : (
                    daPagina.map((r: DayAttendanceRow) => {
                      const situacao = r.beforeCutoff && r.paymentSituation !== "Pago" ? "Anterior ao corte" : r.paymentSituation;
                      const cor = situacaoCor[situacao];
                      const detalhePagamento = [r.paymentMethods, r.paymentDate ? `em ${dataBr(r.paymentDate)}` : ""].filter(Boolean).join(" · ");
                      return (
                        <Box key={r.scheduleId} className="c4u-table__row">
                          <Box className="admin-plans-cell" data-label="Data" sx={{ flex: "0 0 104px" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{dataBr(r.start)}</Typography>
                              <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{hora(r.start)}–{hora(r.end)}</Typography>
                            </Box>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: "1 1 170px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{nome(r.patientName)}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--md" data-label="Profissional / serviço" sx={{ flex: "1 1 190px", minWidth: 0 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{nome(r.professionalName)}</Typography>
                              <Typography sx={{ fontSize: 12, color: "#6c757d", overflowWrap: "anywhere" }}>{r.serviceName}</Typography>
                            </Box>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--lg" data-label="Convênio / plano" sx={{ flex: "0 1 130px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{r.planName}</Typography>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Status" sx={{ flex: "0 0 176px", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                            <StatusChip label={r.confirmation} color={r.confirmationColor} />
                            <StatusChip label={r.presence} color={r.presenceColor} />
                          </Box>
                          <Box className="admin-plans-cell" data-label="Pagamento" sx={{ flex: "0 0 170px", minWidth: 0 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                            {cor ? (
                              <Chip size="small" label={situacao} sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600, bgcolor: cor.bg, color: cor.fg }} />
                            ) : (
                              <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{situacao}</Typography>
                            )}
                            {detalhePagamento && (
                              <Typography sx={{ fontSize: 11.5, color: "#6c757d", mt: 0.25, overflowWrap: "anywhere" }}>{detalhePagamento}</Typography>
                            )}
                            </Box>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Valor final" sx={{ flex: "0 0 104px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", width: "100%" }}>
                              <Typography sx={{ fontSize: 13 }}>{moeda(r.finalValue)}</Typography>
                              {r.discount > 0 && <Typography sx={{ fontSize: 11, color: "#6c757d" }}>desc. {moeda(r.discount)}</Typography>}
                            </Box>
                          </Box>
                          <Box className="admin-plans-cell admin-plans-actions" sx={{ flex: "0 0 92px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <Tooltip title={r.paymentId ? "Abrir recibo" : "Sem recibo: pagamento não passou pelo caixa"} arrow>
                              <span>
                                <IconButton className="c4u-row-action" size="small" disabled={!r.paymentId} onClick={() => r.paymentId && abrirRecibo(r.paymentId)} sx={{ bgcolor: "#03a9f4", color: "white", "&:hover": { bgcolor: "#0288d1" } }} aria-label="Abrir recibo">
                                  <Receipt fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {podeAbrirFinanceiro && (
                              <Tooltip title="Financeiro do paciente" arrow>
                                <span>
                                  <IconButton className="c4u-row-action" size="small" disabled={!r.patientId} onClick={() => abrirFinanceiro(r.patientId)} sx={{ bgcolor: colors.primary, color: "white", "&:hover": { bgcolor: colors.primaryHover } }} aria-label="Financeiro do paciente">
                                    <PointOfSale fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>

                {/* Impressão: o relatório mantém as colunas separadas (pedido do Hilton, 28/09/2026) */}
                <Box className="c4u-table" sx={{ display: "none", "@media print": { display: "block" } }}>
                  <Box className="c4u-table__header">
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 86px" }}>Data</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 92px" }}>Horário</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "1 1 170px" }}>Paciente</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 150px" }}>Profissional</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "1 1 150px" }}>Serviço</Box>
                    <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "1 1 130px" }}>Convênio / plano</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 1 140px" }}>Status</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 92px" }}>Pagamento</Box>
                    <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 150px" }}>Forma</Box>
                    <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 92px" }}>Pago em</Box>
                    <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px", textAlign: "right" }}>Valor final</Box>
                    <Box className="admin-plans-header-cell admin-plans-actions no-print" sx={{ flex: "0 0 60px" }}>Recibo</Box>
                  </Box>

                  {daPagina.length === 0 ? (
                    <Typography sx={{ px: 2, py: 3, fontSize: 13, color: "#adb5bd", textAlign: "center" }}>
                      Nenhum atendimento neste dia com os filtros escolhidos.
                    </Typography>
                  ) : (
                    daPagina.map((r: DayAttendanceRow) => {
                      const situacao = r.beforeCutoff && r.paymentSituation !== "Pago" ? "Anterior ao corte" : r.paymentSituation;
                      const cor = situacaoCor[situacao];
                      return (
                        <Box key={r.scheduleId} className="c4u-table__row">
                          <Box className="admin-plans-cell" data-label="Data" sx={{ flex: "0 0 86px" }}>{dataBr(r.start)}</Box>
                          <Box className="admin-plans-cell" data-label="Horário" sx={{ flex: "0 0 92px" }}>{hora(r.start)}–{hora(r.end)}</Box>
                          <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: "1 1 170px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{nome(r.patientName)}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--lg" data-label="Profissional" sx={{ flex: "1 1 150px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{nome(r.professionalName)}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--xl" data-label="Serviço" sx={{ flex: "1 1 150px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{r.serviceName}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--md" data-label="Convênio / plano" sx={{ flex: "1 1 130px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, overflowWrap: "anywhere" }}>{r.planName}</Typography>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Status" sx={{ flex: "0 1 140px", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                            <StatusChip label={r.confirmation} color={r.confirmationColor} />
                            <StatusChip label={r.presence} color={r.presenceColor} />
                          </Box>
                          <Box className="admin-plans-cell" data-label="Pagamento" sx={{ flex: "0 0 92px" }}>
                            {cor ? (
                              <Chip size="small" label={situacao} sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600, bgcolor: cor.bg, color: cor.fg }} />
                            ) : (
                              <Typography sx={{ fontSize: 12, color: "#6c757d" }}>{situacao}</Typography>
                            )}
                          </Box>
                          <Box className="admin-plans-cell c4u-col--lg" data-label="Forma" sx={{ flex: "1 1 150px", minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, overflowWrap: "anywhere" }}>{r.paymentMethods || "—"}</Typography>
                          </Box>
                          <Box className="admin-plans-cell c4u-col--xl" data-label="Pago em" sx={{ flex: "0 0 92px" }}>
                            {r.paymentDate ? dataBr(r.paymentDate) : "—"}
                          </Box>
                          <Box className="admin-plans-cell" data-label="Valor final" sx={{ flex: "0 0 100px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {moeda(r.finalValue)}
                            {r.discount > 0 && <Typography sx={{ fontSize: 11, color: "#6c757d" }}>desc. {moeda(r.discount)}</Typography>}
                          </Box>
                          <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 60px" }}>
                            {r.paymentId ? (
                              <Tooltip title="Abrir recibo" arrow>
                                <IconButton className="c4u-btn-icon" onClick={() => abrirRecibo(r.paymentId!)} sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: "#029AAB" } }} aria-label="Abrir recibo">
                                  <Receipt sx={{ fontSize: "1rem", color: "#fff" }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>—</Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Box className="no-print">
                  <StandardPagination
                    currentPage={pagina}
                    totalPages={Math.max(1, Math.ceil(linhas.length / porPagina))}
                    itemsPerPage={porPagina}
                    totalItems={linhas.length}
                    onPageChange={setPagina}
                    onItemsPerPageChange={(n) => { setPorPagina(n); setPagina(1); }}
                  />
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportDayAttendances;
